import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { SITE_BG, SURFACE } from "../../../dark-theme/palette";
import Item from "../../components/Item";
import { usePagesTranslation } from "../../locales";

/**
 * Explains French character normalization in puzzle grids.
 * Accented characters (é, è, ê, etc.) are displayed as their base letter (e) in the grid.
 * Applies to Word Search, Hidden Message, and Crossword puzzles.
 */
export default function HowToFrenchNormalization() {
    const { t } = usePagesTranslation();

    return (
        <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
            <Box sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
                <Item sx={{ p: { xs: 2, md: 4 }, textAlign: "left", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                    {/* Title */}
                    <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 600, color: "#1976d2" }}>
                        {t("howto.frenchNorm.title")}
                    </Typography>

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("howto.frenchNorm.intro-title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t("howto.frenchNorm.intro-p1")}
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("howto.frenchNorm.section-accents")}
                        </Typography>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                                {t("howto.frenchNorm.section-info")}
                            </Typography>
                        </Alert>
                        <Typography variant="body1" component="div" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }} dangerouslySetInnerHTML={{ __html: t("howto.frenchNorm.section-p") }} />
                        <Paper sx={{ p: 2, backgroundColor: SURFACE.sunken, mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("howto.hyphens.example")}
                            </Typography>
                            <Typography variant="body1" component="div" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                <span dangerouslySetInnerHTML={{ __html: t("howto.frenchNorm.example-word") }} />
                                <br />
                                <span dangerouslySetInnerHTML={{ __html: t("howto.frenchNorm.example-grid") }} />
                            </Typography>
                        </Paper>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("howto.frenchNorm.mappings-title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t("howto.frenchNorm.mappings-p")}
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.frenchNorm.mapping-E")}</Typography></li>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.frenchNorm.mapping-A")}</Typography></li>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.frenchNorm.mapping-I")}</Typography></li>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.frenchNorm.mapping-O")}</Typography></li>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.frenchNorm.mapping-U")}</Typography></li>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.frenchNorm.mapping-C")}</Typography></li>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("howto.hyphens.why-matters")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t("howto.frenchNorm.why-p")}
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.frenchNorm.list-clues")}</Typography></li>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.frenchNorm.puzzle-grid")}</Typography></li>
                            <li><Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>{t("howto.frenchNorm.validation")}</Typography></li>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("howto.hyphens.applies-to")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                            {t("howto.frenchNorm.applies-p")}
                        </Typography>
                    </Box>
                </Item>
            </Box>
        </Box>
    );
}
